// Tysiąc online (owner 2026-10-29: the full game to 1000; 20 s a decision, 30 s for the 2 cards the declarer gives): a
// store with the offline store's interface (1000/store.ts), fed by the server's frames (server/ws/cards/thousand.js,
// whose top lists the moves), so Table1000.tsx and the score window work unchanged. The deal (and a redeal), the
// musik flying to the declarer, each card given and a full trick are animated from the change between two states; the
// server waits for each of them.
import { giveTo, playCard, sameCard, type HCard, type State1000 } from '../1000/rules';
import { COLLECT_MS, DEAL_MS, GIVE_FLY_MS, MUSIK_FLY_MS, TRICK_MS, set1000Store, store1000, type Gift, type Store1000, type TableState } from '../1000/store';
import { SLOW } from '../hearts/store';
import { HIDDEN, expand, seatNamesFor, clockOf, type CardEndFrame, type CardStart, type CardStateFrame, type OnlineCardGame, type OnlineDeps } from './common';
import { renamed } from './common';

/** The server's turned view as the rules' state: the others' hands, the face-down musik and the cards given to the
 *  others as face-down stand-ins, no pattern. */
function toState(v: Record<string, unknown>): State1000 {
  return {
    ...(v as unknown as State1000),
    hands: (v.hands as unknown[]).map(expand),
    musik: expand(v.musik),
    given: expand(v.given),
    pattern: [],
  };
}

/** OnlineDeps names only Makao's and Kierki's windows (common.ts); Tysiąc's score window is one of the app's too. */

export function createOnlineThousand(deps: OnlineDeps): OnlineCardGame & { store: Store1000 } {
  const empty: TableState = { game: null, seconds: 0, deal: 0, collecting: false, gift: 0, lastGift: null, playedByPlayer: 0, demo: false, names: null, startedAt: 0, endedAt: null, deadlineAt: null };
  let state: TableState = empty;
  const listeners = new Set<() => void>();
  const timers = new Set<ReturnType<typeof setTimeout>>();
  let clock: ReturnType<typeof setInterval> | null = null;
  let offline: Store1000 | null = null;
  const win = deps;
  /** The server's deal counter (a redeal keeps handNo). */
  let dealNo = -1;
  /** No move until then: the deal, the musik's or a given card's flight (the server refuses moves meanwhile). */
  let busyUntil = 0;
  /** A move went out: no other until the server's answer. */
  let sent = false;
  const set = (patch: Partial<TableState>) => {
    state = { ...state, ...patch };
    for (const fn of listeners) fn();
  };
  const after = (ms: number, fn: () => void) => {
    const h = setTimeout(() => {
      timers.delete(h);
      fn();
    }, ms * SLOW);
    timers.add(h);
  };
  const busy = (ms: number) => {
    busyUntil = Math.max(busyUntil, Date.now() + ms * SLOW);
  };
  const stopTimers = () => {
    for (const h of timers) clearTimeout(h);
    timers.clear();
    if (clock !== null) clearInterval(clock);
    clock = null;
  };
  /** The game when the player may move in `phase` now, else null. */
  const mine = (phase: State1000['phase']) => {
    const g = state.game;
    return g && g.phase === phase && !sent && Date.now() >= busyUntil ? g : null;
  };
  const move = (m: Record<string, unknown>) => {
    sent = true;
    deps.onStep?.(`1000 online: ${String(m.type)}`);
    deps.send(m);
  };
  const wire = (c: HCard) => ({ s: c.s, r: c.r });

  const store: Store1000 = {
    getState: () => state,
    subscribe(fn) {
      listeners.add(fn);
      return () => listeners.delete(fn);
    },
    newGame() {
      // online the server deals
    },
    nextHand() {
      // online the next deal comes by itself
    },
    redeal(yes) {
      const g = mine('redeal');
      if (g && g.asking[0] === 0) move({ type: 'redeal', yes });
    },
    bid(value) {
      const g = mine('bid');
      if (g && g.turn === 0) move({ type: 'bid', value });
    },
    contract(value) {
      const g = mine('contract');
      if (g && g.declarer === 0) move({ type: 'contract', value });
    },
    tap(card) {
      const give = mine('give');
      if (give && give.declarer === 0) return move({ type: 'give', card: wire(card) });
      const g = mine('play');
      if (!g || g.turn !== 0 || !playCard(g, 0, card)) return;
      move({ type: 'play', card: wire(card) });
    },
    stop: stopTimers,
    load() {
      // e2e only, offline
    },
    backdate() {
      // e2e only, offline
    },
  };

  return {
    game: 'thousand',
    store,
    begin(start: CardStart) {
      stopTimers();
      offline ??= store1000();
      // "Play again" at the same table leaves the last game's score window open
      win.closeWindow('t1k_score');
      dealNo = -1;
      busyUntil = 0;
      sent = false;
      state = { ...empty, deal: state.deal, gift: state.gift, names: seatNamesFor(start), ...clockOf(start) };
      set1000Store(store);
      clock = setInterval(() => {
        const g = state.game;
        if (g && g.phase !== 'over' && g.phase !== 'hand-end') set({ seconds: state.seconds + 1 });
      }, 1000);
    },
    state(f: CardStateFrame) {
      const g = toState(f.state);
      const no = Number(f.state.dealNo ?? 0);
      const before = state.game;
      const deadlineAt = f.deadline !== undefined ? Date.now() + f.deadline : null;
      sent = false;
      const playedByPlayer = state.playedByPlayer + (f.last && f.last.seat === 0 && f.last.what === 'play' ? 1 : 0);
      if (!before || no !== dealNo) {
        // a new deal or a redeal: the score window of the last one goes, the view throws the cards out
        dealNo = no;
        win.closeWindow('t1k_score');
        busy(DEAL_MS);
        set({ game: g, deal: state.deal + 1, collecting: false, lastGift: null, deadlineAt });
        return;
      }
      let gift: Partial<TableState> = {};
      if (g.given.length > before.given.length) {
        // a card given: the view flies it (face up only where I gave or got it)
        const to = giveTo(before);
        const card = g.declarer === 0 ? g.given[g.given.length - 1]! : to === 0 ? (g.hands[0]!.find((c) => !before.hands[0]!.some((x) => sameCard(x, c))) ?? HIDDEN) : HIDDEN;
        const lastGift: Gift = { from: g.declarer, to, card };
        gift = { gift: state.gift + 1, lastGift };
        busy(GIVE_FLY_MS + 120);
      }
      // the musik flies to the declarer (the view sees musik go from 3 to 0)
      if (before.phase === 'musik' && g.phase === 'give') busy(MUSIK_FLY_MS + 100);
      set({ game: g, collecting: false, playedByPlayer, deadlineAt, ...gift });
      // a full trick slides to its taker at the end of the server's wait
      if (g.phase === 'trick-done') after(TRICK_MS - COLLECT_MS, () => state.game === g && set({ collecting: true }));
      if (g.phase === 'hand-end') win.openWindow('t1k_score');
      if (g.phase === 'over') set({ endedAt: Date.now() });
    },
    end(f: CardEndFrame) {
      const g = state.game;
      if (g && g.phase !== 'over') set({ game: { ...g, phase: 'over', turn: -1, ...(f.scores ? { scores: f.scores } : {}) }, endedAt: Date.now() });
      set({ deadlineAt: null });
      if (clock !== null) clearInterval(clock);
      clock = null;
      win.openWindow('t1k_score');
    },
    rename(seat, name, bot, flag) {
      set({ names: renamed(state.names, seat, name, bot, flag) });
    },
    stop() {
      stopTimers();
      win.closeWindow('t1k_score');
      if (offline) set1000Store(offline);
      state = { ...empty, deal: state.deal, gift: state.gift };
    },
  };
}
